import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listApiKeys,
  generateApiKey,
  revokeApiKey,
  type ApiKey,
  type ApiKeyCreate,
} from '@littlebooker/dal';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ApiKeysPage() {
  const { account, user } = useAuth();
  const queryClient = useQueryClient();
  const [showNewKey, setShowNewKey] = useState<string | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const isOwner = user?.role === 'owner';

  const { data: apiKeys, isLoading, error } = useQuery({
    queryKey: ['api-keys', account?.id],
    queryFn: () => {
      if (!account?.id) {
        throw new Error('No account ID');
      }
      console.log('🔍 Fetching API keys for account:', account.id);
      return listApiKeys(account.id);
    },
    enabled: !!account?.id && isOwner,
    retry: false,
    onError: (err) => {
      console.error('❌ Error fetching API keys:', err);
    },
    onSuccess: (data) => {
      console.log('✅ API keys fetched:', data?.length || 0, 'keys');
    },
  });

  const generateMutation = useMutation({
    mutationFn: (input: ApiKeyCreate) => {
      if (!account?.id || !user?.id) throw new Error('No account or user ID');
      return generateApiKey(account.id, user.id, input);
    },
    onSuccess: (newKey) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', account?.id] });
      setShowNewKey(newKey.key);
      setNewKeyName('');
      setIsGenerating(false);
    },
    onError: () => {
      setIsGenerating(false);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => {
      if (!account?.id) throw new Error('No account ID');
      return revokeApiKey(keyId, account.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys', account?.id] });
    },
  });

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setIsGenerating(true);
    generateMutation.mutate({ name: newKeyName.trim() });
  };

  const handleCopyKey = (key: string, keyId: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (!account) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">API Keys</h1>
          <p className="text-muted-foreground">No account information available.</p>
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">API Keys</h1>
          <p className="text-muted-foreground">Only account owners can manage API keys.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Account Settings</p>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Key className="h-8 w-8" />
          API Keys
        </h1>
        <p className="text-muted-foreground">
          Generate API keys to authenticate requests to the LittleBooker API. Keys are shown only once when created.
        </p>
      </div>

      {/* New Key Generation Form */}
      <div className="border rounded-lg p-6 bg-card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Generate New API Key
        </h2>
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">
              Key Name
            </label>
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              disabled={isGenerating}
              className="w-full max-w-md rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
              placeholder="e.g., Website API Key, Mobile App Key"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Give your key a descriptive name to identify its purpose.
            </p>
          </div>
          <Button type="submit" disabled={isGenerating || !newKeyName.trim()}>
            <Plus className="mr-2 h-4 w-4" />
            {isGenerating ? 'Generating...' : 'Generate API Key'}
          </Button>
        </form>

        {/* Show newly generated key */}
        {showNewKey && (
          <div className="mt-6 p-4 bg-yellow-badge border border-yellow-badge-foreground/20 rounded-lg">
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-yellow-badge-foreground mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-badge-foreground">
                  Save this key now!
                </p>
                <p className="text-sm text-yellow-badge-foreground/90">
                  This is the only time you'll see the full key. Copy it and store it securely.
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-background border rounded text-sm font-mono break-all">
                {showNewKey}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopyKey(showNewKey, 'new')}
              >
                {copiedKeyId === 'new' ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNewKey(null)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Existing Keys List */}
      <div className="border rounded-lg">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Existing API Keys</h2>
          <p className="text-sm text-muted-foreground">
            Manage your API keys. Revoked keys cannot be restored.
          </p>
        </div>

        {isLoading ? (
          <div className="p-6 text-muted-foreground">Loading...</div>
        ) : error ? (
          <div className="p-6 text-destructive">
            <p className="font-semibold">Error loading API keys</p>
            <p className="text-sm mt-2">{error instanceof Error ? error.message : 'Unknown error'}</p>
            {error instanceof Error && error.stack && (
              <pre className="text-xs mt-4 p-2 bg-muted rounded overflow-auto">
                {error.stack}
              </pre>
            )}
          </div>
        ) : !apiKeys || apiKeys.length === 0 ? (
          <div className="p-6 text-muted-foreground text-center">
            No API keys yet. Generate your first key above.
          </div>
        ) : (
          <div className="divide-y">
            {apiKeys.map((key) => (
              <div key={key.id} className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{key.name}</h3>
                      {key.is_active ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-badge text-green-badge-foreground">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                          Revoked
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{key.key_prefix}...</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() => {
                            // Copy the prefix (we don't have the full key)
                            navigator.clipboard.writeText(key.key_prefix + '...');
                            setCopiedKeyId(key.id);
                            setTimeout(() => setCopiedKeyId(null), 2000);
                          }}
                        >
                          {copiedKeyId === key.id ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                      <div>Created: {formatDate(key.created_at)}</div>
                      <div>Last used: {formatDate(key.last_used_at)}</div>
                      {key.expires_at && (
                        <div>
                          Expires: {formatDate(key.expires_at)}
                          {new Date(key.expires_at) < new Date() && (
                            <span className="ml-2 text-destructive">(Expired)</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {key.is_active && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Are you sure you want to revoke "${key.name}"? This action cannot be undone.`)) {
                          revokeMutation.mutate(key.id);
                        }
                      }}
                      disabled={revokeMutation.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage Instructions */}
      <div className="border rounded-lg p-6 bg-muted/50">
        <h2 className="text-lg font-semibold mb-4">How to Use API Keys</h2>
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium mb-2">1. Copy your API key</p>
            <p className="text-muted-foreground">
              After generating a key, copy it immediately and store it securely. You won't be able to see it again.
            </p>
          </div>
          <div>
            <p className="font-medium mb-2">2. Use in your requests</p>
            <p className="text-muted-foreground mb-2">
              Include the key in your API requests using one of these methods:
            </p>
            <code className="block px-3 py-2 bg-background border rounded text-xs font-mono mb-2">
              x-api-key: lb_live_abc123...
            </code>
            <p className="text-muted-foreground text-xs">
              Or as a Bearer token: <code>Authorization: Bearer lb_live_abc123...</code>
            </p>
          </div>
          <div>
            <p className="font-medium mb-2">3. Security best practices</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Never commit API keys to version control</li>
              <li>Store keys in environment variables (server-side only)</li>
              <li>Use different keys for different environments</li>
              <li>Revoke keys immediately if compromised</li>
            </ul>
          </div>
          <div>
            <p className="font-medium mb-2">⚠️ Important</p>
            <p className="text-muted-foreground">
              For public websites, never expose API keys in client-side code. Use server-side proxy patterns instead.
              See the API documentation for details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
